import React, { useEffect, useState } from "react";
import {
  PieChart,
  Pie,
  Tooltip,
  Cell,
  ResponsiveContainer,
} from "recharts";
import { toast } from "react-toastify";
import EmployeeProcessService from "../services/EmployeeProcessService";
import { useLoading } from "../Utils/LoadingContext";
import "./PlantWiseChart.css";

const COLORS = [
  "#4e79a7", "#f28e2b", "#e15759", "#76b7b2",
  "#59a14f", "#edc949", "#af7aa1", "#ff9da7",
  "#9c755f", "#bab0ac"
];

const STATUS_COLORS = { Present: "#28a745" };

// ----------------- Reusable Plant Card (Plant + Shift wise) -----------------
function PlantCard({ plantID, plantName, chartData, onSliceClick }) {
  if (!chartData || chartData.length === 0) return null;

  const total = chartData.reduce((sum, x) => sum + x.total, 0);
  const totalPP = chartData.reduce((sum, x) => sum + x.count, 0);

  return (
    <div className="card shadow-sm border-0 attendance-card">
      <div className="card-body">
        <h6 className="card-title text-center mb-3">{plantName}</h6>
        <div style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData.filter(x => x.count > 0)}
                dataKey="count"
                nameKey="shiftName"
                outerRadius={100}
                onClick={(data) =>
                  onSliceClick &&
                  onSliceClick(plantID, data.shiftID, plantName, data.shiftName)
                }
              >
                {chartData.filter(x => x.count > 0).map((entry, index) => (
                  <Cell key={index} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name, props) => [
                  `${props.payload.count} PP / ${props.payload.total} Total`,
                  name,
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="text-center mt-2">
          <small className="text-muted">
            Total: {total} | Present: {totalPP}
          </small>
        </div>
      </div>
    </div>
  );
}

// ----------------- Department Chart -----------------
function DepartmentChart({ plantName, shiftName, chartData }) {
  if (!chartData || chartData.length === 0) return null;

  const totalCount = chartData.reduce((sum, d) => sum + d.total, 0);
  const presentCount = chartData.reduce((sum, d) => sum + d.PP, 0);

  return (
    <div className="card shadow-sm border-0 attendance-card">
      <div className="card-body">
        <h6 className="card-title text-center mb-3">
          {plantName} - {shiftName} (Department-wise)
        </h6>

        <div className="d-flex align-items-center">
          {/* Left-side legend */}
          <div style={{ minWidth: "220px", marginRight: "16px" }}>
            {chartData.map((dept, index) => (
              <div key={index} className="d-flex align-items-center mb-1">
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    backgroundColor: COLORS[index % COLORS.length],
                    marginRight: "6px",
                  }}
                ></div>
                <span>
                  <strong>{dept.departmentName}</strong> ({dept.PP}/{dept.total})
                </span>
              </div>
            ))}
          </div>

          {/* Pie Chart */}
          <div style={{ height: 400, flex: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData.filter((d) => d.PP > 0)}
                  dataKey="PP"
                  nameKey="departmentName"
                  outerRadius={160}
                  innerRadius={70}
                  label={false}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={index} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name, props) => {
                    const { PP, total } = props.payload;
                    return [`Total: ${total} | Present: ${PP}`, name];
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Footer totals */}
        <div className="text-center mt-3">
          <small className="text-muted">
            Total: {totalCount} | Present: {presentCount}
          </small>
        </div>
      </div>
    </div>
  );
}

// ----------------- Main Component -----------------
export default function PlantWisePie() {
  const { setLoading } = useLoading();
  const [plantCharts, setPlantCharts] = useState([]);
  const [deptCharts, setDeptCharts] = useState([]);
  const [showPlant, setShowPlant] = useState(true);
  const [deptPlant, setDeptPlant] = useState(null);
  const [deptShift, setDeptShift] = useState(null);

  useEffect(() => {
    fetchPlantShiftWise();
  }, []);

  // Fetch Plant + Shift wise PP data
  const fetchPlantShiftWise = async () => {
    try {
      setLoading(true);
      const response = await EmployeeProcessService.getAttendanceChart(
        "1",
        "PlantWiseAttendance",
        "1"
      );
      const rawData = response?.data?.result || [];
      const data = transformPlantShiftWise(rawData);
      console.log("Plant + Shift Data:", data); // debug
      setPlantCharts(data);
      setShowPlant(true);
      setDeptCharts([]);
      setDeptPlant(null);
      setDeptShift(null);
    } catch (err) {
      toast.error("Error loading plant-shift-wise charts");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch Department-wise PP data
  const fetchDepartmentWise = async (plantID, shiftID, plantName, shiftName) => {
    try {
      setLoading(true);
      const response = await EmployeeProcessService.getAttendanceChart(
        plantID,
        "DepartmentWisePlantSpecific",
        shiftID
      );
      const rawData = response?.data?.result || [];
      const data = transformDepartmentWise(rawData);
      console.log("Department Data:", data); // debug
      setDeptCharts([{ plantName, shiftName, data }]);
      setShowPlant(false);
      setDeptPlant(plantName);
      setDeptShift(shiftName);
    } catch (err) {
      toast.error("Error loading department-wise charts");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Transform Plant + Shift data for Pie chart
  const transformPlantShiftWise = (data) => {
    const grouped = {};
    data.forEach(row => {
      const plant = row.plantName;
      const shift = row.shiftID;
      if (!plant || !shift) return;

      if (!grouped[plant]) grouped[plant] = {};
      if (!grouped[plant][shift]) grouped[plant][shift] = {
        shiftID: shift,
        shiftName: row.shiftName,
        PP: 0,
        AA: 0,
        plantID: row.plantID
      };

      if (row.label === "PP") grouped[plant][shift].PP += row.count;
      if (row.label === "AA") grouped[plant][shift].AA += row.count;
    });

    return Object.entries(grouped).map(([plantName, shifts]) => ({
      plantName,
      plantID: Object.values(shifts)[0].plantID,
      data: Object.values(shifts).map(s => ({
        shiftID: s.shiftID,
        shiftName: s.shiftName,
        count: s.PP,
        total: s.PP + s.AA,
        color: STATUS_COLORS.Present,
        plantID: s.plantID
      }))
    }));
  };

  // Transform Department-wise data
  const transformDepartmentWise = (data) => {
    const grouped = {};
    data.forEach(row => {
      const dept = row.departmentName || "Unknown";
      if (!grouped[dept]) grouped[dept] = { departmentName: dept, PP: 0, AA: 0, total: 0 };
      if (row.label === "PP") grouped[dept].PP += row.count;
      if (row.label === "AA") grouped[dept].AA += row.count;
      grouped[dept].total = grouped[dept].PP + grouped[dept].AA;
    });
    return Object.values(grouped);
  };

  return (
    <div className="container-fluid py-4">
      <div className="col-12 mb-3 mt-1 border-bottom pb-2 d-flex justify-content-between align-items-center">
        <h5 className="text-left text-primary">
          {showPlant
            ? "Plant + Shift Wise Present Status"
            : `Department Wise - ${deptPlant} (${deptShift})`}
        </h5>
        {!showPlant && (
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={fetchPlantShiftWise}
          >
            Back to Plant Wise
          </button>
        )}
      </div>

      {/* Plant + Shift Wise Charts */}
      {showPlant && (
        <div className="row g-4">
          {plantCharts.map((chart, idx) => (
            <div key={idx} className="col-md-6 col-lg-4">
              <PlantCard
                plantID={chart.plantID}
                plantName={chart.plantName}
                chartData={chart.data}
                onSliceClick={fetchDepartmentWise}
              />
            </div>
          ))}
        </div>
      )}

      {/* Department-wise Charts */}
      {!showPlant &&
        deptCharts.map((chart, idx) => (
          <div key={idx} className="row g-4">
            <div className="col-12">
              <DepartmentChart
                plantName={chart.plantName}
                shiftName={chart.shiftName}
                chartData={chart.data}
              />
            </div>
          </div>
        ))}
    </div>
  );
}
